import { useRequestToken } from '@/store/useRequestToken';
import { UserPortfolio } from '@/types/user-portfolio';
import { getBrandFromHostName } from '@/utils/get-brand-from-host-name';
import { getUserType } from '@/utils/get-user-type';
import { filter, has, keyBy, size } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useMyProfile } from './useAuthUser';
import { useBusinessPortfolioList } from './useBusinessPortfolioList';
import { useCancelledSubscriptions } from './useCancelledSubscriptions';
import { useSendConsentLink } from './useConsentLink';
import { useDealerUserConsent } from './useDealerUserConsent';
import { useGetDisclaimer } from './useGetDisclaimer';
import { useBrokerConfig } from './useLearn';
import { useShortenUrlMutation } from './useShortenUrl';
import { useUserPortfolioList } from './useUserPortfolioList';
export function useHome(partnerKey?: string, userId?: string) {
  const broker = getBrandFromHostName();
  const { data: brokerConfig } = useBrokerConfig(broker);
  const chatBotEnabled =
    brokerConfig?.data?.brokerConfig?.chatBotEnabled ?? false;
  const { data } = useMyProfile();
  const { requestToken } = useRequestToken();
  const { query } = useRouter();

  // Create query params - only include user_id and partner_key when both are provided
  const queryParams =
    partnerKey && userId
      ? {
          userId: userId,
          partnerKey: partnerKey,
        }
      : {};

  const businessPortfoliosHook = useBusinessPortfolioList({
    variables: { status: 'active', broker: broker as any, ...queryParams },
    enabled: !!requestToken || query?.preview !== 'true',
  });
  const userPortfolioHook = useUserPortfolioList({
    variables: {
      status: 'active',
      ...queryParams,
    },
    enabled: !!requestToken || query?.preview !== 'true',
  });
  const userType = getUserType(requestToken);
  const { consent, setConsent } = useDealerUserConsent();
  const { data: disclaimerData } = useGetDisclaimer({
    variables: {
      broker,
      userId: data?.username ?? '',
      userType: userType ?? '',
    },
    enabled:
      !!data?.username &&
      !!userType &&
      userType === 'dealer' &&
      !['paper_trade', 'demo'].includes(broker),
    onSuccess: (data) => {
      if (data?.data?.disclaimerAccepted && consent) {
        toast.success('Disclaimer accepted');
      }
    },
    refetchInterval: (data) =>
      data?.data?.disclaimerAccepted ? false : 10_000,
  });
  const {
    mutateAsync: sendConsentLinkMutate,
    isLoading: isSendConsentLinkLoading,
    isSuccess: isSendConsentLinkSuccess,
  } = useSendConsentLink();
  const { mutateAsync: shortenUrlMutation, isSuccess: shortenUrlSuccess } =
    useShortenUrlMutation();
  useEffect(() => {
    if (shortenUrlSuccess) {
      setConsent(true);
    }
  }, [shortenUrlSuccess, setConsent]);
  const inactiveSubscriptionsHook = useCancelledSubscriptions(
    !!requestToken || query?.preview !== 'true',
  );

  const isLoading =
    userPortfolioHook.isLoading ||
    businessPortfoliosHook.isLoading ||
    userPortfolioHook.isLoading ||
    inactiveSubscriptionsHook.isLoading;

  const rebalancablePortfolioCount = useMemo(
    () =>
      size(
        filter(userPortfolioHook.data?.portfolio, (p: UserPortfolio) =>
          ['t0_pending', 't1_pending', 'new_rebalance_available'].includes(
            p.rebalanceStatus,
          ),
        ),
      ),
    [userPortfolioHook],
  );

  const userPFsKeyedByBPF = keyBy(
    userPortfolioHook.data?.portfolio,
    'portfolioId',
  );

  const nonSubscribedBPFs = filter(
    businessPortfoliosHook.data?.portfolio,
    (bpf) => {
      return !has(userPFsKeyedByBPF, bpf.portfolioId);
    },
  );
  const userPortfolioCount = size(userPortfolioHook.data?.portfolio);

  return {
    isLoading,
    profile: data,
    allBusinessPFs: businessPortfoliosHook.data?.portfolio ?? [],
    businessPortfolios: nonSubscribedBPFs,
    userPortfolios: userPortfolioHook.data?.portfolio ?? [],
    rebalancablePortfolioCount,
    userPortfolioCount,
    cancelledPortfolios: inactiveSubscriptionsHook?.data?.portfolio ?? [],
    cancelledPortfolioCount: size(inactiveSubscriptionsHook?.data?.portfolio),
    chatBotEnabled,
    isDisclaimerAccepted: disclaimerData?.data?.disclaimerAccepted ?? false,
    sendConsentLinkMutate,
    shortenUrlMutation,
    userType,
    brokerConfig,
    consent,
  };
}
